import React, { useEffect, useMemo, useState } from 'react';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import { departmentApi } from '@/features/workspace/api';
import { DepartmentCabinetGrid } from './components/DepartmentCabinetGrid';
import { DepartmentLabCabinetGrid } from './components/DepartmentLabCabinetGrid';
import { DepartmentWorkspaceOverlay } from './components/DepartmentWorkspaceOverlay';
import {
  SuperAiModal,
  RequestGatewayModal,
} from './components/DepartmentModals';
import { RoleAcademicConnectionsOverlay } from '@/features/communications';
import { RoleMyRequestsOverlay } from '@/features/work-requests';
import { ResearchToolsPage } from '@/features/research-tools';
import { FileHubPage } from '@/features/file-hub';
import { EnuSourcesOverlay } from '@/features/student/components/EnuSourcesOverlay';
import { MeetingsDutyPage } from '@/features/owner/components/MeetingsDutyPage';
import {
  CabinetWorkspaceFrame,
  ScientificGpsBlock,
} from '@/features/cabinet/CabinetWorkspaceFrame';
import { routes } from '@/lib/routes';

const WORKSPACE_VIEWS = new Set([
  'overview',
  'monitoring',
  'staff',
  'tasks',
  'surveys',
  'documents',
  'analytics',
  'events',
]);

const MODAL_PATHS: Record<string, string> = {
  my_requests: 'my-requests',
  academic_connections: 'connections',
  research_tools: 'tools',
  files_hub: 'files',
  enu_sources: 'enu-sources',
};

function resolveCabinetHome(pathname: string): string {
  if (pathname === routes.departmentLab || pathname.startsWith(`${routes.departmentLab}/`)) {
    return routes.departmentLab;
  }
  return routes.departmentHead;
}

export const DepartmentHeadPage: React.FC = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const cabinetBase = useMemo(() => resolveCabinetHome(pathname), [pathname]);
  const isLabRole = cabinetBase === routes.departmentLab;
  const roleTitle = isLabRole ? 'Лаборант кафедры' : 'Руководитель подразделения';
  const roleContext = isLabRole ? 'DEPARTMENT_ASSISTANT' : 'DEPARTMENT_HEAD';
  const backToCabinet = () => navigate(cabinetBase);

  const [summary, setSummary] = useState<any>(null);
  const [, setLoading] = useState<boolean>(true);

  const [isSuperAiOpen, setIsSuperAiOpen] = useState<boolean>(false);
  const [isRequestGatewayOpen, setIsRequestGatewayOpen] = useState<boolean>(false);

  const loadSummary = async () => {
    try {
      setLoading(true);
      const res = await departmentApi.summary();
      setSummary(res);
    } catch {
      setSummary({
        department_name: 'Кафедра казахского языкознания',
        role_title: roleTitle,
        kpis: {
          doctoral_count: 12,
          staff_count: 6,
          risk_count: 4,
          avg_progress: 74,
        },
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSummary();
  }, []);

  const handleOpenWorkspace = (defaultView = 'overview') => {
    const view = WORKSPACE_VIEWS.has(defaultView) ? defaultView : 'overview';
    navigate(`${cabinetBase}/${view}`);
  };

  const handleOpenModal = (key: string) => {
    const path = MODAL_PATHS[key];
    if (path) navigate(`${cabinetBase}/${path}`);
  };

  const renderWorkspace = (view: string) => (
    <DepartmentWorkspaceOverlay
      currentView={view}
      onChangeView={(next) => {
        const target = WORKSPACE_VIEWS.has(next) ? next : 'overview';
        navigate(`${cabinetBase}/${target}`);
      }}
      onClose={backToCabinet}
      roleTitle={roleTitle}
      summary={summary}
      onOpenSuperAi={() => setIsSuperAiOpen(true)}
    />
  );

  const cabinetIndex = (
    <CabinetWorkspaceFrame
      links={{
        notebook: `${cabinetBase}/files`,
        calendar: `${cabinetBase}/meetings`,
        meetings: `${cabinetBase}/meetings`,
        connections: `${cabinetBase}/connections`,
        alliances: `${cabinetBase}/connections`,
        qa: routes.portalSection('instructions'),
        support: routes.portalSection('contacts'),
      }}
    >
      {isLabRole ? (
        <DepartmentLabCabinetGrid
          roleTitle={roleTitle}
          onOpenWorkspace={handleOpenWorkspace}
          onOpenSuperAi={() => setIsSuperAiOpen(true)}
          onOpenRequestGateway={() => setIsRequestGatewayOpen(true)}
          onOpenModal={handleOpenModal}
        />
      ) : (
        <DepartmentCabinetGrid
          roleTitle={roleTitle}
          onOpenWorkspace={handleOpenWorkspace}
          onOpenSuperAi={() => setIsSuperAiOpen(true)}
          onOpenRequestGateway={() => setIsRequestGatewayOpen(true)}
          onOpenModal={handleOpenModal}
        />
      )}
      <ScientificGpsBlock openTo={`${cabinetBase}/overview`} />
    </CabinetWorkspaceFrame>
  );

  return (
    <div className="min-h-screen bg-[#07121c] text-slate-100 flex flex-col role-cabinet-page">
      <Routes>
        <Route index element={cabinetIndex} />

        <Route path="overview" element={renderWorkspace('overview')} />
        <Route path="monitoring" element={renderWorkspace('monitoring')} />
        <Route path="staff" element={renderWorkspace('staff')} />
        <Route path="tasks" element={renderWorkspace('tasks')} />
        <Route path="surveys" element={renderWorkspace('surveys')} />
        <Route path="documents" element={renderWorkspace('documents')} />
        <Route path="analytics" element={renderWorkspace('analytics')} />
        <Route path="events" element={renderWorkspace('events')} />

        <Route
          path="my-requests"
          element={
            <RoleMyRequestsOverlay isOpen onClose={backToCabinet} roleContext={roleContext} />
          }
        />
        <Route
          path="connections"
          element={
            <RoleAcademicConnectionsOverlay
              isOpen
              onClose={backToCabinet}
              roleContext={roleContext}
              roleTitle={roleTitle}
            />
          }
        />
        <Route
          path="tools/*"
          element={<ResearchToolsPage onClose={backToCabinet} onBack={backToCabinet} />}
        />
        <Route path="meetings" element={<MeetingsDutyPage onClose={backToCabinet} />} />
        <Route path="meetings/*" element={<MeetingsDutyPage onClose={backToCabinet} />} />
        <Route
          path="files/*"
          element={<FileHubPage onClose={backToCabinet} onBack={backToCabinet} />}
        />
        <Route
          path="enu-sources"
          element={<EnuSourcesOverlay isOpen onClose={backToCabinet} />}
        />
      </Routes>

      <SuperAiModal
        isOpen={isSuperAiOpen}
        onClose={() => setIsSuperAiOpen(false)}
        activeRole={roleContext}
      />

      <RequestGatewayModal
        isOpen={isRequestGatewayOpen}
        onClose={() => setIsRequestGatewayOpen(false)}
        activeRole={roleContext}
        roleLabel={roleTitle}
      />
    </div>
  );
};
